import { Icon, Stack } from '@mui/material';
import { ConductType, ConductTypeSelectOption } from '@schooly/api';
import { useFlag } from '@schooly/hooks/use-flag';
import { ArchiveIcon, ArrowDownV2Icon, Counter, TypographyWithOverflowHint } from '@schooly/style';
import { FC, useMemo } from 'react';

import { DropdownSelectExpandableRow } from '../DropdownSelectExpandableRow';
import { DropdownSelectRow } from '../DropdownSelectRow';
import { DottedDivider } from '../PropertyType/DottedDivider.styled';

export type ConductTypeSelectRowProps = {
  isSelected: boolean;
  conductType: ConductType;
  selectedConductTypeOptions: string[];
  onSelectConductType: (id: string) => void;
  onSelectConductTypeOption: (conductTypeId: string, label: string) => void;
};

export const ConductTypeSelectRow: FC<ConductTypeSelectRowProps> = ({
  isSelected,
  conductType,
  selectedConductTypeOptions,
  onSelectConductType,
  onSelectConductTypeOption,
}) => {
  const [isExpanded, expand, collapse] = useFlag(false);

  const { options, activeOptions, archivedOptions } = useMemo(() => {
    const options = conductType.select_type_options ?? [];

    const [activeOptions, archivedOptions] = options.reduce<
      [ConductTypeSelectOption[], ConductTypeSelectOption[]]
    >(
      (acc, option) => {
        if (option.archived) acc[1].push(option);
        else acc[0].push(option);
        return acc;
      },
      [[], []],
    );

    return { options, activeOptions, archivedOptions };
  }, [conductType.select_type_options]);

  const isExpandable = options.length > 0;

  const renderOption = (option: ConductTypeSelectOption) => {
    const isOptionSelected =
      isSelected || selectedConductTypeOptions.some((label) => label === option.label);

    return (
      <DropdownSelectRow
        key={`${conductType.id}-${option.label}`}
        isSelected={isOptionSelected}
        onClick={() => onSelectConductTypeOption(conductType.id, option.label)}
        sx={(theme) => ({
          '&:hover': {
            backgroundColor: theme.palette.background.default,
            '& .MuiTypography-root, .MuiIcon-root': {
              color: theme.palette.primary.main,
            },
          },
        })}
      >
        <Stack direction="row" gap={1} flex={1}>
          {option.archived && (
            <Icon sx={{ color: !isOptionSelected ? 'common.grey2' : undefined }}>
              <ArchiveIcon />
            </Icon>
          )}
          <TypographyWithOverflowHint
            color={!isOptionSelected ? 'common.grey2' : undefined}
            variant="h3"
            noWrap
          >
            {option.label}
          </TypographyWithOverflowHint>
        </Stack>
      </DropdownSelectRow>
    );
  };

  const rowContent = (
    <Stack flexDirection="row" alignItems="center" gap={0.5}>
      <TypographyWithOverflowHint
        color={!isSelected ? 'common.grey2' : undefined}
        variant="h3"
        noWrap
      >
        {conductType.name}
      </TypographyWithOverflowHint>
      {isExpandable && (
        <Stack
          flexDirection="row"
          alignItems="center"
          gap={1}
          sx={{
            cursor: 'pointer',
            transition: 'all .2s',
            '&:hover': { opacity: 0.5 },
            color: isSelected ? 'primary.main' : undefined,
          }}
          onClick={(e) => {
            e.stopPropagation();
            isExpanded ? collapse() : expand();
          }}
        >
          <Counter
            className="counter"
            inverse
            sx={(theme) => ({
              minWidth: 20,
              textAlign: 'center',
              marginLeft: 0,
              padding: theme.spacing(0, 0.5),
              background: 'transparent',
              borderColor: isSelected ? theme.palette.primary.main : theme.palette.common.grey2,
              color: isSelected ? theme.palette.primary.main : theme.palette.common.grey2,
            })}
          >
            {selectedConductTypeOptions.length ? `${selectedConductTypeOptions.length}/` : ''}
            {options.length}
          </Counter>
          <Icon
            className="arrowIcon"
            sx={(theme) => ({
              width: theme.spacing(1),
              transform: isExpanded ? 'rotate(180deg)' : undefined,
              path: { stroke: isExpanded ? theme.palette.primary.main : theme.palette.common.grey },
            })}
          >
            <ArrowDownV2Icon />
          </Icon>
        </Stack>
      )}
    </Stack>
  );

  const expandedContent = isExpanded ? (
    <Stack gap={1} mt={1}>
      {activeOptions.map(renderOption)}
      {!!archivedOptions.length && <DottedDivider />}
      {archivedOptions.map(renderOption)}
    </Stack>
  ) : undefined;

  return (
    <DropdownSelectExpandableRow
      key={conductType.id}
      onClick={() => onSelectConductType(conductType.id)}
      isSelected={isSelected}
      expandedContent={expandedContent}
      sx={(theme) => ({
        '&:hover': {
          backgroundColor: theme.palette.background.default,
          '& .MuiTypography-root': {
            color: theme.palette.primary.main,
          },
          '& .counter': {
            borderColor: theme.palette.primary.main,
          },
          '& .arrowIcon': {
            path: { stroke: isExpanded ? theme.palette.primary.main : theme.palette.common.grey2 },
          },
        },
      })}
    >
      {rowContent}
    </DropdownSelectExpandableRow>
  );
};
